import React, { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { useResourceInfo } from '../../../../hooks/useResourceInfo';
import CustomTooltip from '../../../../components/common/CustomTooltip';

interface Task {
    item_id: string | number;
    task_title: string;
    project_name: string;
    duration: number;
    start: string;
    end: string;
    completion?: number;
    due_date?: string | null;
    status?: string;
    integration_status?: string | null;
    integration_team_name?: string;
    integration_project_name?: string;
    closed_outside_sprint?: boolean;
    stack_rank?: string | number | null;
    integration_item_type?: string;
    story_points?: string | number;
    story_points_computed?: boolean;
    completed_story_points?: string | number;
    completed_story_points_computed?: boolean;
    original_estimate?: string | number;
    original_estimate_computed?: boolean;
    completed_original_estimate?: string | number;
    completed_original_estimate_computed?: boolean;
    responsible_id?: string;
    priority?: string | number | null;
    integration_url?: string;  // Direct link to Azure/Jira work item
    board_column?: string;     // Board column name
}

interface TaskCardTooltipContentProps {
    task: Task;
    statusLabel: string;
    statusColor: string;
    isSprintFilter?: boolean;
    isSingleTeamSelected?: boolean;
}

const formatDate = (value?: string | null) => {
    if (!value) return 'Not set';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'Not set' : parsed.toLocaleDateString();
};

// Copy icon component
const CopyIcon: React.FC<{ isCopied: boolean }> = ({ isCopied }) => {
    if (isCopied) {
        return (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
        );
    }
    return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
    );
};

const TaskCardTooltipContent: React.FC<TaskCardTooltipContentProps> = ({
    task,
    statusLabel,
    statusColor,
    isSprintFilter = false,
    isSingleTeamSelected = false
}) => {
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const { getResourceName } = useResourceInfo();

    // Format stack rank for display
    const stackRankDisplay = task.stack_rank !== null && task.stack_rank !== undefined && task.stack_rank !== ''
        ? task.stack_rank
        : null;

    const copyToClipboard = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    // Get assigned resource name from responsible_id
    const assignedToName = task.responsible_id ? getResourceName(task.responsible_id) : null;

    return (
        <div className="p-3 space-y-1 text-slate-700 dark:text-slate-200 flex flex-col items-start">
            {/* Risk indicator - show for delayed, blocked, or unallocated */}
            {['delayed', 'blocked', 'unallocated'].includes((task.status || '').toLowerCase()) && (
                <div className="text-[11px] text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                    <FiAlertTriangle className="text-red-500" />
                    This is contributing to risk
                </div>
            )}

            {/* Task ID with copy */}
            <div className="flex items-center justify-between w-full group">
                <span
                    className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-700 dark:hover:text-slate-200"
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(String(task.item_id), 'Task ID'); }}
                >
                    Work Item ID: {task.item_id}
                </span>
                <button
                    className={`ml-2 p-1 transition-colors opacity-0 group-hover:opacity-100 ${copiedKey === 'Task ID' ? 'text-green-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(String(task.item_id), 'Task ID'); }}
                >
                    <CopyIcon isCopied={copiedKey === 'Task ID'} />
                </button>
            </div>

            {/* Team/Project - show when in sprint filter with teams */}
            {isSprintFilter && (task.integration_team_name || task.integration_project_name) && (
                <span className="text-xs text-blue-500 dark:text-blue-400">
                    {task.integration_team_name && task.integration_project_name
                        ? `${task.integration_team_name} : ${task.integration_project_name}`
                        : task.integration_team_name || task.integration_project_name}
                </span>
            )}

            {/* Priority */}
            {task.priority && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                    Priority: {task.priority}
                </span>
            )}

            {/* Task Name with copy */}
            <div className="flex items-center justify-between w-full group">
                <span
                    className="text-sm font-medium text-start flex-1 pr-2 cursor-pointer hover:text-slate-800 dark:hover:text-slate-100"
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(task.task_title, 'Task Name'); }}
                >
                    {task.task_title}
                </span>
                <button
                    className={`p-1 transition-colors opacity-0 group-hover:opacity-100 ${copiedKey === 'Task Name' ? 'text-green-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(task.task_title, 'Task Name'); }}
                >
                    <CopyIcon isCopied={copiedKey === 'Task Name'} />
                </button>
            </div>

            {/* Story Points - for user story, bug, spike */}
            {task.story_points && task.story_points !== '0' && ['user story', 'bug', 'spike'].includes(task.integration_item_type?.toLowerCase() || '') && (
                <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                    Story Points: {task.story_points}
                    {task.story_points_computed && (
                        <CustomTooltip title="Story points computed by Cadence Pro" placement="top" arrow>
                            <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                        </CustomTooltip>
                    )}
                    {task.completed_story_points && (
                        <>
                            <span className="text-slate-400 dark:text-slate-500">|</span>
                            <span>Completed: {task.completed_story_points}</span>
                            {task.completed_story_points_computed ? (
                                <CustomTooltip title="Actual Story Points computed by Cadence Pro" placement="top" arrow>
                                    <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                                </CustomTooltip>
                            ) : (
                                <CustomTooltip title="Actual Story Points in Azure DevOps" placement="top" arrow>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-slate-400 dark:text-slate-500 cursor-help hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 16v-4"></path>
                                        <path d="M12 8h.01"></path>
                                    </svg>
                                </CustomTooltip>
                            )}
                        </>
                    )}
                </span>
            )}

            {/* Original Estimate - for task type */}
            {task.original_estimate && task.original_estimate !== '0' && task.integration_item_type?.toLowerCase() === 'task' && (
                <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                    Original Estimate: {task.original_estimate}
                    {task.original_estimate_computed && (
                        <CustomTooltip title="Estimate computed by Cadence Pro" placement="top" arrow>
                            <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                        </CustomTooltip>
                    )}
                    {task.completed_original_estimate && (
                        <>
                            <span className="text-slate-400 dark:text-slate-500">|</span>
                            <span>Completed: {task.completed_original_estimate}</span>
                            {task.completed_original_estimate_computed ? (
                                <CustomTooltip title="Completed Estimate computed by Cadence Pro" placement="top" arrow>
                                    <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                                </CustomTooltip>
                            ) : (
                                <CustomTooltip title="Completed Estimate in Azure DevOps" placement="top" arrow>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-slate-400 dark:text-slate-500 cursor-help hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 16v-4"></path>
                                        <path d="M12 8h.01"></path>
                                    </svg>
                                </CustomTooltip>
                            )}
                        </>
                    )}
                </span>
            )}

            {/* Status */}
            <div className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-semibold" style={{ color: statusColor }}>
                    Status: {statusLabel}{task.integration_status && task.integration_status.toLowerCase() !== (task.status || '').toLowerCase() && ` (${task.integration_status.charAt(0).toUpperCase() + task.integration_status.slice(1).toLowerCase()})`}
                </span>
            </div>

            {/* Stack Rank - show when sprint filter is active */}
            {isSprintFilter && stackRankDisplay !== null && (
                <div className="text-xs flex items-center gap-1 text-amber-500 dark:text-amber-400 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    Stack Rank: {stackRankDisplay}
                </div>
            )}

            {/* Duration */}
            <span className="text-xs text-slate-500 dark:text-slate-400">
                Duration: {task.duration}{task.duration === 1 ? ' working day' : ' working days'}
            </span>

            {/* Dates */}
            {task.due_date ? (
                <span className="text-xs text-slate-500 dark:text-slate-400">Due date: {formatDate(task.due_date)}</span>
            ) : (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(task.start)} - {formatDate(task.end)}
                </span>
            )}

            {/* Progress */}
            {typeof task.completion === 'number' && (
                <span className="text-xs text-slate-500 dark:text-slate-400">Progress: {task.completion}%</span>
            )}

            {/* Assigned To - uses responsible_id mapped to resource name */}
            {assignedToName && assignedToName !== '-' && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                    Assigned to: {assignedToName}
                </span>
            )}

            {/* Integration Type - at the end */}
            {task.integration_item_type && (
                <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                    Integration Type: {task.integration_item_type}
                </span>
            )}

            {/* Board Column */}
            {task.board_column && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                    Board Column: <span className="font-medium text-slate-600 dark:text-slate-300">{task.board_column}</span>
                </span>
            )}

            {/* Integration URL - Clickable link to Azure/Jira */}
            {task.integration_url && (
                <a
                    href={task.integration_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open in {task.integration_item_type ? (task.integration_url.includes('azure') || task.integration_url.includes('visualstudio') ? 'Azure DevOps' : 'Jira') : 'Integration'}
                </a>
            )}

            {/* Closed Outside Sprint Info - using calendar check icon */}
            {isSprintFilter && task.closed_outside_sprint && (
                <div className="text-xs mt-1 flex items-center gap-1 text-cyan-500 dark:text-cyan-400 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l2 2 4-4" />
                    </svg>
                    Closed outside sprint
                </div>
            )}
        </div>
    );
};

export default TaskCardTooltipContent;
